import { normalizeName } from '@lds-efsp/shared-contracts';

/**
 * Is the OCR-detected lead-doc class plausibly representable among the doc types
 * the selected case type offers?
 *
 * GENEROUS by design (WOL-335): the caller warns the filer when this returns
 * `false`, so we only return `false` when the class is CLEARLY absent (e.g. a
 * "Complaint" upload when the case type offers only "Plaintiff's Claim"). ANY
 * shared token counts as representable, which tolerates paraphrased policy
 * labels ("Complaint" ⊂ "Complaint - Unlimited Civil") and biases hard against
 * false positives.
 *
 * This is the INVERSE tuning of the shared `matchNameToCode`, which is
 * conservative about *asserting* a match (it returns null when unsure) — reusing
 * it here would false-fire the mismatch banner on valid single-token classes
 * whose policy label has extra words. A miss here just means "no extra
 * guidance", which is the safe default.
 */
export function classRepresentedInDocTypes(
  detectedClass: string,
  docTypes: ReadonlyArray<{ name: string }>,
): boolean {
  const classTokens = new Set(normalizeName(detectedClass).split(' ').filter(Boolean));
  if (classTokens.size === 0) return true; // unknown / empty class → never warn
  return docTypes.some((dt) => {
    const nameTokens = new Set(normalizeName(dt.name).split(' ').filter(Boolean));
    for (const t of classTokens) if (nameTokens.has(t)) return true;
    return false;
  });
}

export interface DocClassWarningInput {
  /** Case-initiation mode only — subsequent filings have different doc rules. */
  isInitiation: boolean;
  /** Number of uploaded documents. */
  documentCount: number;
  /** OCR-detected lead-doc class (e.g. "Complaint"), or null before OCR. */
  detectedLeadDocClass: string | null;
  /** The selected case type's code, or null/undefined when unset. */
  caseTypeCode: string | null | undefined;
  /** True while the doc-type list is being fetched. */
  loadingDocTypes: boolean;
  /** Doc types the selected case type offers (loaded from policy). */
  availableDocTypes: ReadonlyArray<{ name: string }>;
}

/**
 * Should the Step-1 mismatch alert fire? True when a document has been uploaded
 * whose OCR-detected class isn't representable in the doc-type list the selected
 * case type offers (WOL-335).
 *
 * IMPORTANT (the bug this guards): gate on a document being UPLOADED
 * (`documentCount > 0`), NOT on it being marked the lead document. In the
 * cold-start flow — and in Walter's exact repro — the uploaded Complaint isn't
 * flagged as the lead until a doc *type* is assigned, which can't happen for a
 * mismatched case type. Gating on `hasLeadDocument` made the banner never fire
 * in precisely the case it exists for.
 */
export function shouldWarnDocClassNotOffered(input: DocClassWarningInput): boolean {
  return (
    input.isInitiation &&
    input.documentCount > 0 &&
    !!input.detectedLeadDocClass &&
    !!input.caseTypeCode &&
    !input.loadingDocTypes &&
    input.availableDocTypes.length > 0 &&
    !classRepresentedInDocTypes(input.detectedLeadDocClass, input.availableDocTypes)
  );
}
